import React, { useRef, useEffect } from 'react';

/**
 * Full-screen Virgo constellation background effect.
 * Renders as an absolute-positioned canvas covering the entire parent.
 * No labels, no borders — pure celestial aesthetic.
 */
export interface ConstellationProps {
  activeSlide?: number;
}

export const Constellation: React.FC<ConstellationProps> = ({ activeSlide = 0 }) => {
  const ref = useRef<HTMLCanvasElement>(null);
  const activeSlideRef = useRef(activeSlide);

  useEffect(() => {
    activeSlideRef.current = activeSlide;
  }, [activeSlide]);

  useEffect(() => {
    const c = ref.current;
    if (!c) return;

    const ctx = c.getContext('2d');
    if (!ctx) return;

    let w = 0;
    let h = 0;

    // Virgo constellation — 13 main stars with relative coordinates (0–1)
    const VIRGO_STARS = [
      { x: 0.65, y: 0.58, r: 3.0, glow: 18 },   // 0  Spica (brightest)
      { x: 0.48, y: 0.40, r: 2.2, glow: 12 },   // 1  Porrima
      { x: 0.76, y: 0.26, r: 2.2, glow: 12 },   // 2  Vindemiatrix
      { x: 0.62, y: 0.32, r: 1.8, glow: 9 },    // 3  Auva
      { x: 0.34, y: 0.42, r: 1.8, glow: 9 },    // 4  Zaniah
      { x: 0.20, y: 0.36, r: 2.0, glow: 9 },    // 5  Zavijava
      { x: 0.46, y: 0.56, r: 2.0, glow: 10 },   // 6  Heze
      { x: 0.36, y: 0.67, r: 1.6, glow: 8 },    // 7  Syrma
      { x: 0.27, y: 0.74, r: 1.3, glow: 6 },    // 8  Kappa
      { x: 0.18, y: 0.80, r: 1.5, glow: 6 },    // 9  Lambda
      { x: 0.56, y: 0.48, r: 1.6, glow: 8 },    // 10 Theta
      { x: 0.74, y: 0.66, r: 1.8, glow: 8 },    // 11 Mu
      { x: 0.84, y: 0.72, r: 1.5, glow: 6 },    // 12 109 Vir
    ];

    const VIRGO_LINES = [
      [5, 4],   // Zavijava → Zaniah
      [4, 1],   // Zaniah → Porrima
      [1, 3],   // Porrima → Auva
      [3, 2],   // Auva → Vindemiatrix
      [1, 10],  // Porrima → Theta
      [10, 0],  // Theta → Spica
      [0, 6],   // Spica → Heze
      [6, 7],   // Heze → Syrma
      [7, 8],   // Syrma → Kappa
      [8, 9],   // Kappa → Lambda
      [0, 11],  // Spica → Mu
      [11, 12], // Mu → 109 Vir
    ];

    // Ambient floating particles
    const particles = Array.from({ length: 50 }, () => ({
      x: Math.random() * 2000,
      y: Math.random() * 2000,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
      r: 0.5 + Math.random() * 1.2,
      phase: Math.random() * Math.PI * 2,
      twinkleSpeed: 0.8 + Math.random() * 3.0,
    }));

    // Interpolation variables for transitions
    let currentAngle = 0;
    let currentScale = 1.0;
    let currentX = 0;
    let currentY = 0;

    const getSlideParams = (slide: number) => {
      switch (slide) {
        case 0:  return { angle: 0, scale: 1.0, x: 0, y: 0 }; // Hero
        case 1:  return { angle: Math.PI / 4, scale: 0.85, x: 0.1, y: -0.1 }; // Gallery: 45deg, slightly right-up
        case 2:  return { angle: -Math.PI / 6, scale: 1.15, x: -0.1, y: 0.1 }; // Channels: -30deg, zoomed in, left-down
        case 3:  return { angle: Math.PI / 2, scale: 0.9, x: 0.05, y: 0.05 }; // Links: 90deg, offset
        case 4:  return { angle: -Math.PI / 3, scale: 1.05, x: -0.05, y: -0.05 }; // Contact: -60deg
        case 5:  return { angle: Math.PI * 0.6, scale: 0.95, x: -0.12, y: 0.08 }; // News
        case 6:  return { angle: -Math.PI * 0.45, scale: 1.1, x: 0.08, y: -0.12 }; // VIP
        case 7:  return { angle: Math.PI * 0.8, scale: 1.0, x: 0.0, y: 0.1 }; // Subscribe
        case 8:  return { angle: -Math.PI * 0.8, scale: 0.85, x: -0.08, y: -0.08 }; // Help
        case 9:  return { angle: Math.PI * 0.15, scale: 1.0, x: 0.06, y: 0.06 }; // Profile
        case 10: return { angle: -Math.PI * 0.15, scale: 0.9, x: -0.06, y: 0.12 }; // Register
        case 11: return { angle: Math.PI * 0.35, scale: 1.05, x: 0.04, y: -0.04 }; // Wishlist
        default: return { angle: 0, scale: 1.0, x: 0, y: 0 };
      }
    };

    let mouse = { x: -999, y: -999, active: false };

    const onMove = (e: MouseEvent) => {
      const r = c.getBoundingClientRect();
      mouse.x = e.clientX - r.left;
      mouse.y = e.clientY - r.top;
      mouse.active = true;
    };

    const onLeave = () => {
      mouse.active = false;
    };

    window.addEventListener('mousemove', onMove);
    document.addEventListener('mouseleave', onLeave);

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = c.offsetWidth;
      h = c.offsetHeight;
      c.width = w * dpr;
      c.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Re-scatter particles to fill the new canvas size
      for (const p of particles) {
        p.x = Math.random() * w;
        p.y = Math.random() * h;
      }
    };

    const ro = new ResizeObserver(resize);
    ro.observe(c);
    resize();

    let raf = 0;

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      const time = performance.now() * 0.001;

      // 1. Animate ambient floating particles
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > w) p.vx *= -1;
        if (p.y < 0 || p.y > h) p.vy *= -1;

        const twinkle = 0.12 + 0.88 * ((Math.sin(time * p.twinkleSpeed + p.phase) + 1) * 0.5);
        ctx.fillStyle = `rgba(255, 255, 255, ${twinkle * 0.55})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      // 2. Calculate focus point
      let focusX: number;
      let focusY: number;
      if (mouse.active) {
        focusX = mouse.x;
        focusY = mouse.y;
      } else {
        focusX = w * 0.5 + Math.sin(time * 0.4) * (w * 0.22);
        focusY = h * 0.5 + Math.cos(time * 0.3) * (h * 0.16);
      }

      // 3. Update interpolation towards targets
      const target = getSlideParams(activeSlideRef.current);
      currentAngle += (target.angle - currentAngle) * 0.05;
      currentScale += (target.scale - currentScale) * 0.05;
      currentX += (target.x - currentX) * 0.05;
      currentY += (target.y - currentY) * 0.05;

      // 4. Compute Virgo star positions with gentle float, rotation, scale, and offset
      const computedStars = VIRGO_STARS.map((star, idx) => {
        const dx = Math.sin(time * 0.7 + idx * 1.5) * 4;
        const dy = Math.cos(time * 0.55 + idx * 2.2) * 4;
        
        // Relative to center (0.5, 0.5)
        const rx = star.x - 0.5;
        const ry = star.y - 0.5;
        
        // Apply rotation and scale
        const rotX = (rx * Math.cos(currentAngle) - ry * Math.sin(currentAngle)) * currentScale;
        const rotY = (rx * Math.sin(currentAngle) + ry * Math.cos(currentAngle)) * currentScale;
        
        // Translate back and apply slide offsets
        const px = (0.5 + rotX + currentX) * w + dx;
        const py = (0.5 + rotY + currentY) * h + dy;
        
        return {
          px,
          py,
          r: star.r,
          glow: star.glow,
        };
      });

      // 5. Draw constellation lines — subtle pulsing glow (pastel red #DC394B)
      const linePulse = 0.18 + 0.12 * Math.sin(time * 1.8);
      ctx.lineWidth = 1;
      for (const [a, b] of VIRGO_LINES) {
        const s = computedStars[a];
        const e = computedStars[b];
        ctx.strokeStyle = `rgba(220, 57, 75, ${linePulse})`;
        ctx.beginPath();
        ctx.moveTo(s.px, s.py);
        ctx.lineTo(e.px, e.py);
        ctx.stroke();
      }

      // 6. Mouse/focus → nearby star connections (pastel red for stars, dark red for dust)
      const connectionDist = 150;
      const allPoints = [
        ...computedStars.map(s => ({ x: s.px, y: s.py })),
        ...particles.map(p => ({ x: p.x, y: p.y })),
      ];
      // Also connect nearby particles to each other (like the original effect)
      const allWithFocus = [...allPoints, { x: focusX, y: focusY }];
      for (let i = 0; i < allWithFocus.length; i++) {
        for (let j = i + 1; j < allWithFocus.length; j++) {
          const dx = allWithFocus[i].x - allWithFocus[j].x;
          const dy = allWithFocus[i].y - allWithFocus[j].y;
          const d = Math.hypot(dx, dy);
          if (d < connectionDist) {
            const alpha = (1 - d / connectionDist);
            // Stronger for constellation stars (first 13 entries)
            const isStarPair = i < 13 || j < 13;
            if (isStarPair) {
              ctx.strokeStyle = `rgba(220, 57, 75, ${alpha * 0.35})`;
              ctx.lineWidth = 0.8;
            } else {
              ctx.strokeStyle = `rgba(125, 27, 36, ${alpha * 0.12})`;
              ctx.lineWidth = 0.5;
            }
            ctx.beginPath();
            ctx.moveTo(allWithFocus[i].x, allWithFocus[i].y);
            ctx.lineTo(allWithFocus[j].x, allWithFocus[j].y);
            ctx.stroke();
          }
        }
      }

      // 7. Draw Virgo stars — glow halos + bright cores
      for (const star of computedStars) {
        const dToFocus = Math.hypot(star.px - focusX, star.py - focusY);
        const isNear = dToFocus < 60;
        const currentGlow = star.glow + (isNear ? 6 : 0);

        // Radial glow: white core -> pastel red glow -> dark red outer glow -> transparent
        const grad = ctx.createRadialGradient(
          star.px, star.py, 0,
          star.px, star.py, currentGlow,
        );
        grad.addColorStop(0, `rgba(255, 255, 255, ${isNear ? 0.95 : 0.8})`);
        grad.addColorStop(0.2, `rgba(220, 57, 75, ${isNear ? 0.55 : 0.35})`);
        grad.addColorStop(1, 'rgba(92, 20, 26, 0)');

        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(star.px, star.py, currentGlow, 0, Math.PI * 2);
        ctx.fill();

        // White core
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(star.px, star.py, star.r * 0.6, 0, Math.PI * 2);
        ctx.fill();
      }

      raf = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseleave', onLeave);
      ro.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={ref}
      className="absolute inset-0 w-full h-full block pointer-events-none"
      style={{ zIndex: 1 }}
    />
  );
};

export default Constellation;
